import {
  addDays,
  averageEnding,
  compareDates,
  goalProgress,
  latestEntry,
  liveEntries,
  weeklyRate,
  weightTrend,
  type GoalProgress,
  type LocalDate,
  type TrendPoint,
  type WeightEntryLike,
  type WeightSettings,
  type WeightUnit,
} from '@tahi/core';

export const TREND_DAYS = 30;
export const RECENT_COUNT = 14;

export interface WeightOverview {
  today: LocalDate;
  unit: WeightUnit;
  settings: WeightSettings;
  latest: WeightEntryLike | null;
  todayEntry: WeightEntryLike | null;
  /** 7-day average ending today (null with no weigh-in this week). */
  avg7: number | null;
  /** This week's average minus last week's (null unless both exist). */
  avgChange: number | null;
  /** Grams a week; null until there's enough data. */
  rate: number | null;
  trend: TrendPoint[];
  goal: GoalProgress | null;
  /** Every live entry, oldest first. */
  entries: WeightEntryLike[];
  /** Newest first. */
  recent: WeightEntryLike[];
  count: number;
}

export const MIN_TREND_DAYS = 7;

/**
 * The chart starts at the first weigh-in (at least 7 days, at most 30), so a new user's few
 * points spread across the chart instead of sitting in its right edge.
 */
function trendDays(live: readonly WeightEntryLike[], today: LocalDate): number {
  const first = live[0];
  if (!first) return MIN_TREND_DAYS;
  for (let n = MIN_TREND_DAYS; n < TREND_DAYS; n += 1) {
    if (compareDates(addDays(today, -(n - 1)), first.date) <= 0) return n;
  }
  return TREND_DAYS;
}

export function buildWeightOverview(
  entries: readonly WeightEntryLike[],
  settings: WeightSettings,
  today: LocalDate,
): WeightOverview {
  const live = liveEntries(entries);
  const latest = latestEntry(live);
  const avg7 = averageEnding(live, today);
  const prev = averageEnding(live, addDays(today, -7));
  return {
    today,
    unit: settings.unit,
    settings,
    latest,
    todayEntry: live.find((e) => e.date === today) ?? null,
    avg7,
    avgChange: avg7 !== null && prev !== null ? avg7 - prev : null,
    rate: weeklyRate(live, today),
    trend: weightTrend(live, today, trendDays(live, today)),
    goal: goalProgress(settings, avg7 ?? latest?.valueG ?? null),
    entries: live,
    recent: live.slice(-RECENT_COUNT).reverse(),
    count: live.length,
  };
}
